import { useCallback, useEffect, useRef, useState } from "react";
import {
  Activity, Database, Boxes, Timer, CreditCard, Plug, RefreshCw, CheckCircle2, AlertTriangle, XCircle, MinusCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { HEALTH } from "@/constants/testIds";
import api from "@/lib/api";

const STATUS_STYLE = {
  ok: { cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", Icon: CheckCircle2, label: "Healthy" },
  warning: { cls: "text-amber-400 border-amber-500/40 bg-amber-500/10", Icon: AlertTriangle, label: "Warning" },
  stale: { cls: "text-amber-400 border-amber-500/40 bg-amber-500/10", Icon: AlertTriangle, label: "Stale" },
  degraded: { cls: "text-red-400 border-red-500/40 bg-red-500/10", Icon: XCircle, label: "Degraded" },
  unreachable: { cls: "text-red-400 border-red-500/40 bg-red-500/10", Icon: XCircle, label: "Unreachable" },
  unavailable: { cls: "text-red-400 border-red-500/40 bg-red-500/10", Icon: XCircle, label: "Unavailable" },
  external: { cls: "text-sky-400 border-sky-500/40 bg-sky-500/10", Icon: MinusCircle, label: "External" },
  idle: { cls: "text-muted-foreground border-border/60 bg-muted/40", Icon: MinusCircle, label: "Idle" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.idle;
  return (
    <Badge variant="outline" className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${s.cls}`}>
      <s.Icon className="mr-1 h-3 w-3" /> {s.label}
    </Badge>
  );
}

function timeAgo(iso) {
  if (!iso) return "never";
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

function Card({ title, icon: Icon, status, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border/60 bg-card/40 p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Icon className="h-4 w-4 text-accent" /> {title}
        </div>
        {status && <StatusBadge status={status} />}
      </div>
      <div className="mt-3 text-xs text-muted-foreground">{children}</div>
    </div>
  );
}

export default function Health() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/system/health");
      setData(data);
    } catch {
      setData({ status: "degraded", mongo: { status: "unreachable" } });
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000);
    return () => clearInterval(timer.current);
  }, [load]);

  const overall = data?.status || "idle";

  return (
    <div data-testid={HEALTH.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-6xl px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">System health</h1>
              {data && <StatusBadge status={overall} />}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Live status of Mongo, the semantic index, background workers, billing webhooks and connectors.
              {data?.checked_at && <span className="ml-1">Updated {timeAgo(data.checked_at)}.</span>}
            </p>
          </div>
          <Button data-testid={HEALTH.refresh} variant="outline" size="sm" onClick={load} disabled={refreshing} className="rounded-full border-border/70 text-xs uppercase tracking-[0.12em]">
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {!data ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
          </div>
        ) : (
          <>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Card title="MongoDB" icon={Database} status={data.mongo?.status} testid={HEALTH.cardMongo}>
                Primary datastore connectivity (ping).
              </Card>
              <Card title="Semantic index (FAISS)" icon={Boxes} status={data.faiss?.status} testid={HEALTH.cardFaiss}>
                {data.faiss?.vectors ?? 0} vectors indexed for glossary search.
              </Card>
              <Card title="Job runner" icon={Timer} status={data.job_runner?.status} testid={HEALTH.cardJob}>
                Background schema + glossary tasks.
              </Card>
              <Card title="Schedule runner" icon={Timer} status={data.schedule_runner?.status} testid={HEALTH.cardSchedule}>
                Scheduled report delivery · heartbeat {timeAgo(data.schedule_runner?.last_heartbeat)}.
              </Card>
              <Card title="Alert runner" icon={Activity} status={data.alert_runner?.status} testid={HEALTH.cardAlert}>
                Alert evaluation loop · heartbeat {timeAgo(data.alert_runner?.last_heartbeat)}.
              </Card>
              <Card title="Razorpay webhook" icon={CreditCard} status={data.razorpay_webhook_last_received_at ? "ok" : "external"} testid={HEALTH.cardRazorpay}>
                {data.razorpay_webhook_last_received_at ? `Last event ${timeAgo(data.razorpay_webhook_last_received_at)}.` : "No events received yet (external dependency)."}
              </Card>
            </div>

            <div className="mt-6 rounded-2xl border border-border/60 bg-card/40 p-5" data-testid={HEALTH.connectors}>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Plug className="h-4 w-4 text-accent" /> Connectors
                <span className="text-accent">{data.connectors?.length || 0}</span>
              </div>
              {(!data.connectors || data.connectors.length === 0) ? (
                <p className="mt-3 text-xs text-muted-foreground">No data sources connected yet.</p>
              ) : (
                <div className="mt-3 space-y-1.5">
                  {data.connectors.map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-lg border border-border/40 bg-background/60 px-3 py-2 text-xs">
                      <span className="font-semibold text-foreground">{c.name || "(unnamed)"}</span>
                      <Badge variant="outline" className="rounded-full px-1.5 py-0 text-[9px] text-muted-foreground">{c.kind}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
